import type { AssignmentTemplate } from '../../model/assignments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssignmentTemplateDuplicateApiOptions {
  templateId: string | number
  onStart?: () => void
  onSuccess?: (template: AssignmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `POST …/{template_id}/duplicate`. Metadata-only copy: title gains `" (copy)"`, status forced to
 *  `DRAFT`, `data` copied verbatim — the two rows share the same objects in the bucket. */
export const useAssignmentTemplateDuplicateApi = async (
  options: AssignmentTemplateDuplicateApiOptions,
): Promise<AssignmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assignment-templates.duplicate', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template: AssignmentTemplate | null = data.data?.template ?? null

    // A `201` with no template is a broken contract.
    if (!template) {
      const msg = 'Unexpected error. The assignment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    toast.success('Assignment template duplicated')
    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
